import React, { useEffect, useMemo, useState } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { useAuth } from 'contexts/AuthContext';

import CustomSelect from 'components/form/CustomSelect';
import DateTimePicker from 'components/form/DateTimePicker';
import TextArea from 'components/form/TextArea';
import TicketSelect from 'components/form/TicketSelect';
import UserSelect from 'components/form/UserSelect';
import { MSG_MAX_LENGTH, MSG_REQUIRED } from 'constant/ValidationMessages';
import { MasterTypeResult } from 'stores/MasterTypeStoreAdapter';
import {
  CreateApprovalTicketParams,
  CreateTaskTicketParams,
  TicketListResult,
  createApprovalTicket,
  createTaskTicket,
  listTicket,
} from 'stores/TicketStoreAdapter';
import {
  GetTicketTypeResponse,
  TicketTypeSummaryResult,
  getTicketType,
} from 'stores/TicketTypeStoreAdapter';
import { successDefault, toErrorDetail } from 'utils/Toastify';

import TicketFormFields, { buildFormValues } from './TicketFormFields';

type Props = {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
  /** 選択可能な有効種別（一覧から取得済みを渡す） */
  activeTypes: TicketTypeSummaryResult[];
  /** 優先度マスタ（API9001: priority） */
  priorities: MasterTypeResult[];
  /**
   * 子チケット作成モード。指定時は種別を固定し parentTicketId を送る。
   * 親の分類/種別を継承する。
   */
  parent?: { ticketId: string; typeId: string; managementId: string };
  /** 初期選択する種別ID（スプリットボタンで選択済みの種別を引き継ぐ） */
  initialTypeId?: string;
};

/**
 * チケット作成ダイアログ。
 * 種別を選ぶと分類（TASK/APPROVAL）が決まり、対応する入力フォームを表示する（D10-2-7）。
 * - タスク: 優先度・予定日入力あり・承認者なし
 * - 承認: 承認者選択あり・優先度/予定日なし
 * 担当者未指定時は作成者が担当になる旨を明示（D10-2-6）。
 */
const TicketCreateDialog: React.FC<Props> = ({
  open,
  onClose,
  onCreated,
  activeTypes,
  priorities,
  parent,
  initialTypeId,
}) => {
  const { userSession, users } = useAuth();

  const [typeId, setTypeId] = useState('');
  const [typeDetail, setTypeDetail] = useState<GetTicketTypeResponse | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('');
  const [assigneeUserId, setAssigneeUserId] = useState<string | null>(null);
  const [approverUserId, setApproverUserId] = useState<string | null>(null);
  const [scheduledStartDate, setScheduledStartDate] = useState('');
  const [scheduledEndDate, setScheduledEndDate] = useState('');
  const [formValues, setFormValues] = useState<Record<string, string>>({});
  const [submitAndSend, setSubmitAndSend] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  // 親チケット選択（通常作成時のみ・子作成モードでは parent 固定）
  const [parentTicketId, setParentTicketId] = useState<string | null>(null);
  const [parentCandidates, setParentCandidates] = useState<TicketListResult[]>([]);

  // 親指定時は親の種別に固定
  const effectiveTypeId = parent ? parent.typeId : typeId;

  // 初期化
  useEffect(() => {
    if (!open) return;
    setTypeId(parent ? parent.typeId : (initialTypeId ?? ''));
    setTitle('');
    setDescription('');
    setPriority('');
    setAssigneeUserId(null);
    setApproverUserId(null);
    setScheduledStartDate('');
    setScheduledEndDate('');
    setFormValues({});
    setSubmitAndSend(false);
    setErrors({});
    setParentTicketId(null);
    setParentCandidates([]);
  }, [open, parent, initialTypeId]);

  // 種別選択時にテンプレートを取得
  useEffect(() => {
    if (!effectiveTypeId) {
      setTypeDetail(null);
      return;
    }
    getTicketType({ typeId: effectiveTypeId, visibleOnly: true })
      .then(setTypeDetail)
      .catch(() => setTypeDetail(null));
  }, [effectiveTypeId]);

  // 親候補の取得（通常作成時のみ）。同一種別・親のみ（2段階制約で親を持つものは除外）に絞る。
  useEffect(() => {
    if (parent || !open || !effectiveTypeId) {
      setParentCandidates([]);
      return;
    }
    listTicket({ typeIds: [effectiveTypeId], onlyParent: true })
      .then((res) => setParentCandidates(res.results))
      .catch(() => setParentCandidates([]));
    // 種別を変えたら選択済みの親をクリア
    setParentTicketId(null);
  }, [parent, open, effectiveTypeId]);

  const selectedType = useMemo(
    () => activeTypes.find((t) => t.typeId === effectiveTypeId) ?? null,
    [activeTypes, effectiveTypeId],
  );
  const category = selectedType?.ticketCategory ?? null;
  const isPrivate = selectedType?.scope === 'PRIVATE';

  const typeOptions = useMemo(
    () => activeTypes.map((t) => ({ value: t.typeId, label: t.name })),
    [activeTypes],
  );
  const priorityOptions = useMemo(
    () => priorities.map((p) => ({ value: p.typeCode, label: p.typeLabel })),
    [priorities],
  );

  const formTypeByKey = useMemo(() => {
    const map: Record<string, string> = {};
    if (typeDetail) {
      typeDetail.results.forEach((g) => g.items.forEach((it) => (map[it.itemKey] = it.formType)));
      typeDetail.unassignedItems.forEach((it) => (map[it.itemKey] = it.formType));
    }
    return map;
  }, [typeDetail]);

  const validate = (): boolean => {
    const e: Record<string, string> = {};
    if (!effectiveTypeId) e.typeId = MSG_REQUIRED('種別');
    if (!title.trim()) e.title = MSG_REQUIRED('タイトル');
    else if (title.length > 300) e.title = MSG_MAX_LENGTH('タイトル', 300);
    if (category === 'APPROVAL' && !approverUserId) e.approverUserId = MSG_REQUIRED('承認者');
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = () => {
    if (!validate() || submitting) return;
    setSubmitting(true);
    const values = buildFormValues(formValues, formTypeByKey);

    if (category === 'APPROVAL') {
      const params: CreateApprovalTicketParams = {
        typeId: effectiveTypeId,
        title: title.trim(),
        description: description.trim() || undefined,
        approverUserId: approverUserId as string,
        assigneeUserId: assigneeUserId || undefined,
        parentTicketId: parent?.ticketId ?? parentTicketId ?? undefined,
        formValues: values.length ? values : undefined,
        submit: submitAndSend,
      };
      createApprovalTicket(params)
        .then(() => {
          successDefault('承認チケットを作成しました');
          onCreated();
          onClose();
        })
        .catch((err) => setErrors({ submit: toErrorDetail(err) ?? '作成に失敗しました' }))
        .finally(() => setSubmitting(false));
    } else {
      const params: CreateTaskTicketParams = {
        typeId: effectiveTypeId,
        title: title.trim(),
        description: description.trim() || undefined,
        priority: priority || undefined,
        assigneeUserId: isPrivate ? undefined : assigneeUserId || undefined,
        scheduledStartDate: scheduledStartDate || undefined,
        scheduledEndDate: scheduledEndDate || undefined,
        parentTicketId: parent?.ticketId ?? parentTicketId ?? undefined,
        formValues: values.length ? values : undefined,
      };
      createTaskTicket(params)
        .then(() => {
          successDefault('タスクチケットを作成しました');
          onCreated();
          onClose();
        })
        .catch((err) => setErrors({ submit: toErrorDetail(err) ?? '作成に失敗しました' }))
        .finally(() => setSubmitting(false));
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        elevation: 0,
        sx: { borderRadius: 3, border: 1, borderColor: 'divider', height: '88vh' },
      }}
    >
      <DialogTitle
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}
      >
        <Typography variant="subtitle1" fontWeight={600}>
          {parent ? `子チケット作成（${parent.managementId}）` : 'チケット作成'}
        </Typography>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ overflow: 'auto' }}>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {/* 種別選択（子作成時は固定表示） */}
          {parent ? (
            <TextField
              size="small"
              fullWidth
              label="種別"
              value={selectedType?.name ?? ''}
              disabled
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          ) : (
            <CustomSelect
              mode="single"
              label="種別"
              required
              options={typeOptions}
              value={typeId}
              onChange={setTypeId}
              error={!!errors.typeId}
              helperText={errors.typeId}
              placeholder="種別を選択"
            />
          )}

          {selectedType && (
            <Typography variant="caption" color="text.secondary">
              分類: {category === 'APPROVAL' ? '承認' : 'タスク'} / スコープ:{' '}
              {isPrivate ? 'プライベート' : 'グローバル'}
            </Typography>
          )}

          {/* 親チケット選択（子作成モードは固定表示・通常作成は任意選択） */}
          {parent ? (
            <TextField
              size="small"
              fullWidth
              label="親チケット"
              value={parent.managementId}
              disabled
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          ) : (
            selectedType && (
              <TicketSelect
                tickets={parentCandidates}
                selectedTicketId={parentTicketId}
                onChange={(t) => setParentTicketId(t?.ticketId ?? null)}
                label="親チケット（任意）"
                placeholder="親にするチケットを検索（同じ種別のみ）"
              />
            )
          )}

          {/* 種別未選択時は入力欄を出さず、選択を促す（操作順序を明確化・業務レビュー） */}
          {!selectedType && !parent && (
            <Box
              sx={{
                py: 3,
                px: 2,
                textAlign: 'center',
                border: 1,
                borderColor: 'divider',
                borderRadius: 2,
                borderStyle: 'dashed',
              }}
            >
              <Typography variant="body2" color="text.secondary">
                まず種別を選択してください。
                <br />
                種別に応じて入力フォームが表示されます。
              </Typography>
            </Box>
          )}

          {selectedType && (
            <>
              {/* タイトル */}
              <TextField
                size="small"
                fullWidth
                required
                label="タイトル"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                error={!!errors.title}
                helperText={errors.title}
                slotProps={{ htmlInput: { maxLength: 300 } }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />

              {/* 承認固有: 承認者 */}
              {category === 'APPROVAL' && (
                <UserSelect
                  mode="single"
                  label="承認者"
                  required
                  users={users}
                  selectedUserId={approverUserId}
                  onChange={setApproverUserId}
                  error={!!errors.approverUserId}
                  helperText={errors.approverUserId}
                />
              )}

              {/* 担当者（PRIVATEは非表示・自動で自分） */}
              {!isPrivate && (
                <Box>
                  <UserSelect
                    mode="single"
                    label="担当者"
                    users={users}
                    selectedUserId={assigneeUserId}
                    onChange={setAssigneeUserId}
                  />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mt: 0.5, display: 'block' }}
                  >
                    未指定の場合は自分（{userSession.userName}）が担当になります
                  </Typography>
                </Box>
              )}

              {/* タスク固有: 優先度・スケジュール（予定日） */}
              {category === 'TASK' && (
                <>
                  <CustomSelect
                    mode="single"
                    label="優先度"
                    options={priorityOptions}
                    value={priority}
                    onChange={setPriority}
                    placeholder="優先度を選択（任意）"
                  />
                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontWeight={600}
                      sx={{ mb: 0.5, display: 'block' }}
                    >
                      スケジュール
                    </Typography>
                    <Stack direction="row" spacing={1}>
                      <DateTimePicker
                        mode="start"
                        label="開始予定日"
                        value={scheduledStartDate}
                        onChange={setScheduledStartDate}
                        fullWidth
                      />
                      <DateTimePicker
                        mode="end"
                        label="終了予定日"
                        value={scheduledEndDate}
                        onChange={setScheduledEndDate}
                        fullWidth
                      />
                    </Stack>
                  </Box>
                </>
              )}

              {/* 説明（担当者・スケジュールの下） */}
              <TextArea
                label="説明"
                value={description}
                onChange={setDescription}
                maxLength={10000}
                rows={4}
                fullWidth
              />

              {/* テンプレート自由項目 */}
              {typeDetail && (
                <TicketFormFields
                  formGroups={typeDetail.results}
                  unassignedItems={typeDetail.unassignedItems}
                  values={formValues}
                  onChange={(k, v) => setFormValues((prev) => ({ ...prev, [k]: v }))}
                />
              )}
            </>
          )}

          {errors.submit && <Alert severity="error">{errors.submit}</Alert>}
        </Stack>
      </DialogContent>

      {/* アクション（固定フッター・スクロール不要で常に押せる） */}
      <DialogActions sx={{ px: 3, py: 1.5, borderTop: 1, borderColor: 'divider' }}>
        <Button onClick={onClose} sx={{ textTransform: 'none', fontWeight: 600 }}>
          キャンセル
        </Button>
        {selectedType && category === 'APPROVAL' && (
          <Button
            variant="outlined"
            disabled={submitting}
            onClick={() => {
              setSubmitAndSend(false);
              handleSubmit();
            }}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, boxShadow: 'none' }}
          >
            一時保存
          </Button>
        )}
        <Button
          variant="contained"
          disabled={submitting || !selectedType}
          onClick={() => {
            setSubmitAndSend(category === 'APPROVAL');
            handleSubmit();
          }}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            boxShadow: 'none',
            '&:hover': { boxShadow: 'none' },
          }}
        >
          {category === 'APPROVAL' ? '申請' : '作成'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TicketCreateDialog;
